import { useState, useEffect, useRef, useCallback, ReactNode } from 'react'

import { clsx } from 'clsx'
import { Carousel } from 'react-responsive-carousel'

import { fileSize } from '@bettermode/common/filesize'
import { File } from '@tribeplatform/gql-client/types'
import { useMedia } from '@tribeplatform/react-sdk/hooks'
import { IconButton, Button } from '@tribeplatform/react-ui-kit/Button'
import { useDocument } from '@tribeplatform/react-ui-kit/hooks'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { useKeypressListener } from '@tribeplatform/react-ui-kit/utils'

import { T } from '../i18n/index.js'

import { useMediaModal } from './MediaModalProvider.js'
import { MediaPreview } from './MediaPreview.js'
import { downloadFile } from './utils.js'

export const MediaModal = () => {
  const { document } = useDocument()
  const {
    slidesRef,
    initialSlideIndex,
    closeMediaModal,
    allowDownload,
    title,
  } = useMediaModal()

  const cache = useRef<{ [key: number]: boolean }>({})
  const [downloading, setDownloading] = useState(false)

  const [currentIndex, setCurrentIndex] = useState(initialSlideIndex)

  const onClose = useCallback(() => {
    closeMediaModal()
  }, [closeMediaModal])

  const slides = slidesRef.current

  const slide = slides[currentIndex]
  const length = slides.length ?? 0

  const { data: mediaInfo } = useMedia({
    variables: {
      id: slide.id,
    },
    fields: {
      onFile: 'basic',
      onImage: 'basic',
    },
    useQueryOptions: {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!slide.id,
    },
  })

  const handleDownload = useCallback(async () => {
    const downloadSpecs = (() => {
      if (mediaInfo) {
        return {
          downloadUrl: (mediaInfo as File).downloadUrl,
          name: (mediaInfo as File).name,
        }
      }
      return {
        downloadUrl: slide?.url || slide?.downloadUrl,
        name: slide.name,
      }
    })()

    if (!downloadSpecs.downloadUrl) {
      return
    }

    setDownloading(true)
    if (downloadSpecs.name) {
      await downloadFile(
        document,
        downloadSpecs.downloadUrl,
        downloadSpecs.name,
      )
    } else {
      await downloadFile(document, downloadSpecs.downloadUrl)
    }

    setDownloading(false)
  }, [mediaInfo, slide?.url, slide?.downloadUrl, slide.name, document])

  useEffect(() => {
    return () => {
      // clear the cache on unmount
      cache.current = {}
    }
  }, [])

  useKeypressListener('Escape', onClose, 'keyup')

  useKeypressListener('ArrowRight', () => {
    if (document.dir === 'rtl') {
      if (currentIndex > 0) {
        setCurrentIndex(currentIndex - 1)
      }
    } else if (currentIndex < length - 1) {
      setCurrentIndex(currentIndex + 1)
    }
  })

  useKeypressListener('ArrowLeft', () => {
    if (document.dir === 'rtl') {
      if (currentIndex < length - 1) {
        setCurrentIndex(currentIndex + 1)
      }
    } else if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
    }
  })

  const mediaSize = (slide as File)?.size || (mediaInfo as File)?.size

  return (
    <Modal
      size="full"
      open
      onClose={onClose}
      defaultZenState
      showZenModeBtn={false}
      panelClassName="h-screen flex flex-col overflow-y-hidden"
    >
      <Modal.Content
        className={clsx(
          'bg-surface',
          'w-screen h-screen p-0 sm:px-0 my-0',
          'flex overflow-hidden items-center justify-center',
        )}
      >
        <div className="flex flex-col min-w-0 flex-1 h-full">
          <SlideActionBar
            title={title}
            allowDownload={allowDownload}
            currentSlideIndex={currentIndex}
            slidesCount={length}
            setSlideIndex={setCurrentIndex}
            isDownloading={downloading}
            handleDownload={handleDownload}
            onClose={onClose}
            mediaSize={mediaSize}
          />
          <div className="flex min-h-0 h-full">
            <div
              className="relative min-w-0 flex-1 [&>*]:h-full [&_.carousel]:h-full [&_.slider-wrapper]:h-full [&_.slider]:flex [&_.slide]:flex [&_ul]:h-full "
              dir="ltr"
            >
              <Carousel
                selectedItem={currentIndex}
                dynamicHeight={false}
                showStatus={false}
                infiniteLoop={false} // This should remain false otherwise the lazy-load won't work
                showThumbs={false}
                showIndicators={false}
                showArrows={false}
                transitionTime={200}
                useKeyboardArrows={false}
                swipeScrollTolerance={64}
                preventMovementUntilSwipeScrollTolerance
                autoFocus
                onChange={index => {
                  setCurrentIndex(index)
                }}
              >
                {slides.map((slide, index) => {
                  if (cache.current[index] || index === currentIndex) {
                    cache.current[currentIndex] = true
                    return (
                      <MediaPreview
                        key={slide.id || slide.name || index}
                        media={slide}
                        isActive={currentIndex === index}
                        onDownload={handleDownload}
                      />
                    )
                  }
                  /**
                   * Return an skeleton instead of MediaPreview for off-screen content that
                   * are not loaded yet, to improve first render performance.
                   */
                  return (
                    <div
                      className="flex items-center justify-center"
                      key={slide.id || slide.name}
                    />
                  )
                })}
              </Carousel>
            </div>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  )
}

type SlideActionBarProps = {
  title: ReactNode | null
  allowDownload: boolean
  currentSlideIndex: number
  slidesCount: number
  setSlideIndex: (slideIndex: number) => void
  mediaSize?: number
  isDownloading: boolean
  handleDownload: () => void
  onClose: () => void
}

const SlideActionBar = ({
  title,
  allowDownload,
  currentSlideIndex,
  slidesCount,
  setSlideIndex,
  mediaSize,
  isDownloading,
  handleDownload,
  onClose,
}: SlideActionBarProps) => {
  return (
    <div className="max-w-screen-2xl w-full mx-auto p-5 sm:p4 flex items-center justify-between">
      {title}
      <div className="w-full ms-auto sm:w-auto justify-between sm:justify-end flex space-x-4">
        {slidesCount > 1 && (
          <div className="flex items-center">
            <PrevSlideButton
              slidesCount={slidesCount}
              currentIndex={currentSlideIndex}
              setSlideIndex={setSlideIndex}
            />
            <span className="whitespace-nowrap text-sm text-center w-[52px]">
              <T
                defaultMessage="{current_slide_index} / {slides_count}"
                id="MediaModal.SlideCounter"
                values={{
                  current_slide_index: currentSlideIndex + 1,
                  slides_count: slidesCount,
                }}
              />
            </span>
            <NextSlideButton
              slidesCount={slidesCount}
              currentIndex={currentSlideIndex}
              setSlideIndex={setSlideIndex}
            />
          </div>
        )}
        <div className="flex items-center space-x-2 ms-auto">
          {allowDownload && (
            <Button
              disabled={isDownloading}
              size="md"
              leadingIcon={<SvgIcon name="download" />}
              loading={isDownloading}
              onClick={handleDownload}
              variant="secondaryNeutral"
              title={
                mediaSize &&
                fileSize(mediaSize, {
                  spacer: ' ',
                  roundingMethod: 'round',
                  round: 0,
                })
              }
            >
              <T defaultMessage="Download" id="Generics.Download" />
            </Button>
          )}

          <Button
            variant="secondaryNeutral"
            size="md"
            leadingIcon={<SvgIcon name="close" />}
            onClick={onClose}
          >
            <T defaultMessage="Close" id="Generics.Close" />
          </Button>
        </div>
      </div>
    </div>
  )
}

const PrevSlideButton = (props: {
  currentIndex: number
  slidesCount: number
  setSlideIndex: (slideIndex: number) => void
}) => {
  const { currentIndex, slidesCount, setSlideIndex } = props
  const isEnabled = slidesCount > 1 && currentIndex > 0
  if (slidesCount < 2) {
    return null
  }
  return (
    <IconButton
      variant="secondaryNeutral"
      size="md"
      onClick={() => isEnabled && setSlideIndex(currentIndex - 1)}
      disabled={!isEnabled}
      icon={<SvgIcon className="rtl:-scale-x-100" name="arrow-left" />}
    />
  )
}

const NextSlideButton = (props: {
  currentIndex: number
  slidesCount: number
  setSlideIndex: (slideIndex: number) => void
}) => {
  const { currentIndex, slidesCount, setSlideIndex } = props
  const isEnabled = slidesCount > 1 && currentIndex < slidesCount - 1
  if (slidesCount < 2) {
    return null
  }
  return (
    <IconButton
      variant="secondaryNeutral"
      size="md"
      icon={<SvgIcon className="rtl:-scale-x-100" name="arrow-right" />}
      onClick={() => isEnabled && setSlideIndex(currentIndex + 1)}
      disabled={!isEnabled}
    />
  )
}
